import { z } from 'zod'
import { tripPaymentBodySchema } from './money-account'
import { deviceIdSchema, isoDate } from './trip'
import {
  RECEIPT_PARTS_MAX,
  linkReceiptCountrySchema,
  photoReceiptCountrySchema,
  receiptCountrySchema,
  receiptFailureSchema,
  receiptMatchSchema,
  receiptPaidBySchema,
  receiptStatusSchema,
  receiptViaSchema,
} from '#model/entities/receipt'
import { newItemSchema } from '#model/entities/item'
import { serbianReceiptLink } from '#model/entities/receipt-link'
import { newPlaceSchema } from '#model/entities/place'
import { ERROR } from '#model/support/errors'
import { LOCALES } from '#model/support/locale'
import { citySchema } from '#model/values/geo'
import { moneyCodec } from '#model/values/money'
import { rateCodec } from '#model/values/rates'
import { quantityCodec } from '#model/values/units'

/**
 * «Отправить чек» (MOL-125): the receipt is named by the phone, as everything written offline is, so
 * a receipt sent twice from the queue is one receipt; «Переснять» is a new one (П-3). The parts follow
 * one by one, `PUT /receipts/:id/parts/:n`, as JPEG.
 *
 * `language` is the one the lines are to be read out in — the interface's, which the server does not
 * otherwise know (П-4, MOL-55). `country` is where it was bought: it picks the reader's alphabets
 * and the currency.
 */
export const receiptPhotoBodySchema = z.strictObject({
  id: deviceIdSchema,
  parts: z.int().min(1).max(RECEIPT_PARTS_MAX),
  country: photoReceiptCountrySchema,
  language: z.enum(LOCALES),
  capturedAt: isoDate,
})

/** Past any link of the tax office's — 794–878 signs measured (MOL-223) — and short of a flood. */
export const RECEIPT_LINK_LENGTH_MAX = 2_048

/**
 * A Serbian receipt by the link of its QR code (MOL-232), with no photo: the server asks the tax
 * office for its lines. The link is checked at the door as the phone checked it before queueing it.
 */
export const receiptLinkBodySchema = z.strictObject({
  id: deviceIdSchema,
  link: z
    .string()
    .max(RECEIPT_LINK_LENGTH_MAX)
    .refine((link) => serbianReceiptLink(link).ok, { error: ERROR.RECEIPT_LINK_INVALID }),
  country: linkReceiptCountrySchema,
  language: z.enum(LOCALES),
  capturedAt: isoDate,
  /**
   * How the link came, and whether the camera missed in the sheet before it (MOL-234). Optional: a
   * body a phone of an earlier build left in its queue has neither, and a door refused is never sent
   * again (Р-3) — such a receipt is counted as not named.
   */
  via: receiptViaSchema.optional(),
  missed: z.boolean().optional(),
})

export const receiptBodySchema = z.union([receiptPhotoBodySchema, receiptLinkBodySchema])
export type ReceiptBody = z.output<typeof receiptBodySchema>
export type ReceiptPhotoBody = z.output<typeof receiptPhotoBodySchema>
export type ReceiptLinkBody = z.output<typeof receiptLinkBodySchema>

/** What the receipt prints at its head and foot, as read: the seller's tax number is its place (MOL-126). */
export const receiptHeaderCodec = z.strictObject({
  tin: z.string().nullable(),
  date: z.iso.date().nullable(),
  time: z.string().nullable(),
  receiptNo: z.string().nullable(),
  /**
   * The shop's own name, as the tax office names a Serbian receipt's premises — «RODA MEGAMARKET 463»
   * (MOL-232): what a new place is proposed as. A photo's head reads none.
   */
  shop: z.string().nullable(),
})

/**
 * The place a receipt is at (MOL-126): the one its purchases were recorded at, or — before that — the
 * place with the receipt's tax number in its city, if there is one; `null` and the person names it.
 */
export const receiptPlaceCodec = z.strictObject({
  id: z.uuid(),
  name: z.string(),
  city: z.string(),
  tin: z.string().nullable(),
})
export type ReceiptPlace = z.output<typeof receiptPlaceCodec>

/** A line as the receipt printed it, with its figures as the server laid them out. */
export const receiptLineCodec = z.strictObject({
  printed: z.string(),
  hs: z.string().nullable(),
  sku: z.string().nullable(),
  quantity: quantityCodec.nullable(),
  price: moneyCodec.nullable(),
  sum: moneyCodec.nullable(),
  discount: moneyCodec.nullable(),
  /** The line's own arithmetic holds: quantity × price = paid + discount. */
  settled: z.boolean(),
  /**
   * Put right by the digits the till's font confuses, on a model's reading the lines then proved whole
   * (MOL-290): «проверьте», never as read. Sent only when it is, and only to a phone that asked by
   * `RECEIPT_REPAIRED_HEADER` — an installed app of an earlier build reads a line strictly.
   */
  repaired: z.boolean().optional(),
})
export type ReceiptLineView = z.output<typeof receiptLineCodec>

/**
 * A line on the review screen (MOL-126): as read, with the item it found and how — `weak` is
 * «проверьте» — the gloss, and what it will be recorded at (`amount`, MOL-124 В-5). `rememberedPrice`
 * is the shop's memory's shelf price when its reader may have confused the one read for it — on a receipt the
 * model read up to two 5↔6, on Tesseract's one digit of its pairs (MOL-315) — unless the total proving the
 * receipt points at it — its only line, one piece at its sum, nothing sent to the card (В-1, MOL-314) — or when
 * the line has no price — emptied by the check (MOL-291): a hint, the figures stay as read.
 */
export const receiptReviewLineCodec = z.strictObject({
  ...receiptLineCodec.shape,
  itemId: z.uuid().nullable(),
  itemName: z.string().nullable(),
  match: receiptMatchSchema,
  translation: z.string().nullable(),
  /**
   * The new item's name in `translation` spells a word the dictionary has not in Russian letters (MOL-293,
   * В-4): offered, and taken away by one press. Sent only when it is, and only to a phone that asked by
   * `RECEIPT_SPELLED_HEADER` — an installed app of an earlier build reads a line strictly.
   */
  spelled: z.boolean().optional(),
  /**
   * What one piece of the till holds by the line's name (MOL-264): the purchase is the till's pieces times it,
   * the till's own figures stay as printed. Sent only when there is one, and only to a phone that asked by
   * `RECEIPT_PACK_HEADER` — an installed app of an earlier build reads a line strictly.
   */
  pack: quantityCodec.optional(),
  amount: moneyCodec.nullable(),
  rememberedPrice: moneyCodec.nullable(),
  /**
   * The package's code the Serbian tax office gave the line, which no item holds yet (MOL-234):
   * «Привязать штрихкоды?» asks about it at «Записать», never in silence (MOL-100). Sent only when there
   * is one, and only to a phone that asked by `RECEIPT_CODES_HEADER` — an installed app of an earlier
   * build reads a line strictly (adversarial А3).
   */
  code: z.string().optional(),
})
export type ReceiptReviewLine = z.output<typeof receiptReviewLineCodec>

/** A receipt in «Покупки»: where it is, what was read of its head, how much is there to check. */
export const receiptSummaryCodec = z.strictObject({
  id: z.uuid(),
  status: receiptStatusSchema,
  failure: receiptFailureSchema.nullable(),
  /** Parts of its photo; none for a receipt by its link (MOL-232). */
  parts: z.int().min(0).max(RECEIPT_PARTS_MAX),
  /** Parts the server holds; fewer than `parts` while it is `uploading`. */
  received: z.int().min(0).max(RECEIPT_PARTS_MAX),
  capturedAt: isoDate,
  country: receiptCountrySchema,
  language: z.enum(LOCALES),
  header: receiptHeaderCodec.nullable(),
  total: moneyCodec.nullable(),
  /** The lines add up to the printed total. */
  balanced: z.boolean(),
  /**
   * Its lines: as read, before it is recorded; once recorded, only those that are purchases — a line not
   * recorded goes at «Записать», and one whose purchase was removed with it (MOL-240).
   */
  lineCount: z.int().min(0),
  /**
   * Lines to check: whose own arithmetic does not hold — highlighted, never refused (Р-3 of MOL-113) — and those
   * the font's swaps put right (MOL-290).
   */
  unsettled: z.int().min(0),
  place: receiptPlaceCodec.nullable(),
  /**
   * The purchases it was recorded as (MOL-126); `null` before. Never after: the trip removed for good takes
   * the receipt with it (MOL-240).
   */
  tripId: z.uuid().nullable(),
})
export type ReceiptSummary = z.output<typeof receiptSummaryCodec>

export const receiptsResponseCodec = z.strictObject({ receipts: z.array(receiptSummaryCodec) })
export type ReceiptsResponse = z.output<typeof receiptsResponseCodec>

/** The same receipt recorded before (MOL-126 Т-11): «Этот чек уже записан». */
export const receiptDuplicateCodec = z.strictObject({
  receiptId: z.uuid(),
  tripId: z.uuid(),
  recordedAt: isoDate,
})

/**
 * One receipt with its lines, in the order printed, and what the review needs beside them: the rate
 * of the receipt's day from its currency's pair with the person's income currency, as a trip snapshots
 * one (`null` — nothing to convert, or nothing known), and the same receipt recorded before.
 */
export const receiptDetailCodec = z.strictObject({
  receipt: receiptSummaryCodec,
  /**
   * A line's position is its place here — true of a receipt not recorded, the only one the phone draws.
   * A recorded one keeps only the lines that are purchases (MOL-240), so its list has gaps no position
   * names: never read a recorded receipt's line by its place, nor a code's `position` against it.
   */
  lines: z.array(receiptReviewLineCodec),
  rate: rateCodec.nullable(),
  duplicateOf: receiptDuplicateCodec.nullable(),
  /**
   * The total as the reader found it in one place, which no second source vouched for (MOL-244): never
   * the receipt's total, only what «Прочитали не всё» measures the lines against. Sent only to a phone
   * that asked by `RECEIPT_READ_TOTAL_HEADER` — an installed app of an earlier build reads the detail
   * strictly, as with a line's `code`.
   */
  readTotal: moneyCodec.nullable().optional(),
  /**
   * Read in part, as the server measured it at the reading (MOL-255, А6 «а»): of the lines as read, before
   * the check emptied any — a phone that sees only the lines shown cannot tell an emptied line from a
   * lost one. `null` for a receipt read before. Sent only to a phone that asked by
   * `RECEIPT_READ_PARTLY_HEADER`, as `readTotal`.
   */
  readPartly: z.boolean().nullable().optional(),
  /**
   * What a card sent to the card instead of taking it off (MOL-289, «Զեղչ գործարքից»): the lines make the
   * total less it — `linesTotalOf` — and the total is what left the card. With it the bonus the receipt is
   * partly paid with, which the trip's money takes off the total. Sent only to a phone that asked by
   * `RECEIPT_CASHBACK_HEADER`, as `readTotal`.
   */
  cashback: moneyCodec.nullable().optional(),
  redeemed: moneyCodec.nullable().optional(),
  /**
   * How the receipt was paid, as its words of payment say (MOL-312): cash or a card, only where one of
   * them carries the sum — `null` when the receipt does not say, or says both. It suggests an account,
   * never chooses one. Sent only to a phone that asked by `RECEIPT_PAID_BY_HEADER`, as `readTotal`.
   */
  paidBy: receiptPaidBySchema.nullable().optional(),
})
export type ReceiptDetail = z.output<typeof receiptDetailCodec>

/**
 * A line as it is to be recorded (MOL-126): every line of the receipt once, by its position. Left out —
 * «не записываем», still in «Строки» (MOL-124 Р-5). Recorded — an item of the catalogue, or a new one by
 * the name the person settled on (created by the rules of MOL-12), the quantity and what was paid for
 * the line, «цена за всё, как в чеке»: the unit price is the server's to work out.
 */
const receiptRecordItemSchema = z.union([
  z.strictObject({ id: z.uuid() }),
  z.strictObject({ name: newItemSchema.shape.name }),
])

export const receiptRecordLineSchema = z.discriminatedUnion('skip', [
  z.strictObject({ position: z.int().min(0), skip: z.literal(true) }),
  z.strictObject({
    position: z.int().min(0),
    skip: z.literal(false),
    item: receiptRecordItemSchema,
    quantity: quantityCodec.nullable(),
    amount: moneyCodec.nullable(),
  }),
])

/**
 * A line the reader lost, typed on the review (MOL-273): no position — it is none of the receipt's —
 * and nothing printed; only the item is required, as for any purchase.
 */
export const receiptAddedLineSchema = z.strictObject({
  item: receiptRecordItemSchema,
  quantity: quantityCodec.nullable(),
  amount: moneyCodec.nullable(),
})
export type ReceiptAddedLine = z.output<typeof receiptAddedLineSchema>

/** How many lines a review may add: a receipt holds at most 500, and what is lost is a part of it. */
export const RECEIPT_ADDED_MAX = 100

/**
 * «Записать» (MOL-126): the whole receipt as the phone holds it, never its edits alone — the server's
 * suggestions may have changed since it was looked at (Р-4). The trip is named by the phone, as every
 * write offline is (MOL-24): a record sent again from the queue meets its own trip. The place is one
 * of the catalogue, or a new store in a city of the receipt's country — either held to the person's
 * geography; the day is the one the person confirmed — the printed one, or their correction of it —
 * and never one still to come anywhere on Earth (`latestDay`).
 */
export const receiptRecordBodySchema = z.strictObject({
  tripId: deviceIdSchema,
  place: z.union([
    z.strictObject({ id: z.uuid() }),
    z.strictObject({ name: newPlaceSchema.shape.name, city: citySchema }),
  ]),
  purchasedOn: z.iso.date(),
  /**
   * The receipt's total as the phone holds it (owner, В-5 of the review, 03.10.2026): the printed one,
   * or the person's correction of a total OCR misread. Absent — the server takes the printed one where
   * it is not below the lines, else the lines.
   */
  total: moneyCodec.refine((value) => value.minor > 0n, { error: ERROR.INVALID_AMOUNT }).optional(),
  lines: z.array(receiptRecordLineSchema).max(500),
  /**
   * The lines the reader lost and the person typed on the review (MOL-273): purchases of the same trip,
   * apart from `lines`, whose positions stay the receipt's. Absent — none, or a phone of an earlier build.
   */
  added: z.array(receiptAddedLineSchema).max(RECEIPT_ADDED_MAX).optional(),
  /**
   * What the person put right on the review (MOL-222, the measure of 0.2): the positions of the lines
   * whose item they changed from the one the review showed, and of those whose quantity or sum they
   * typed anew. Only the phone knows what it showed — the shops' memory may have learnt meanwhile from
   * another record (adversarial А6) — and a total put right moves no line (review 2). Counted, never
   * recorded: a figure of the measure, not of money. Absent from a phone of an earlier build — the
   * server then compares with the review as it would show it now.
   */
  edited: z
    .strictObject({
      item: z.array(z.int().min(0)).max(500),
      figures: z.array(z.int().min(0)).max(500),
    })
    .optional(),
  /**
   * «Привязать и записать» (MOL-234, owner's В-2 «а»): the positions of the lines whose code the
   * person binds to the item recorded — by the rules of MOL-100. Absent — no question was asked.
   */
  barcodes: z.array(z.int().min(0)).max(500).optional(),
  /**
   * The account the receipt was paid from, and «списано» from it (MOL-312): the body of
   * `PUT /trips/:id/payment`, set on the trip in the record's transaction by the same rules. Absent —
   * a phone of an earlier build, or «без счёта» — the trip is on no account.
   */
  account: tripPaymentBodySchema.optional(),
})
export type ReceiptRecordBody = z.output<typeof receiptRecordBodySchema>

/** What «Записать» answers: the receipt, recorded, and its purchases — where the phone goes next. */
export const receiptRecordedCodec = z.strictObject({
  receipt: receiptSummaryCodec,
  tripId: z.uuid(),
  /**
   * What became of each code asked to be bound (MOL-234), only when some were: `written`; `there` —
   * the item held it already; `held` — another item holds it, named, and nothing was written, as
   * MOL-100's 409 says; `full` — the item holds twenty. The record stands whichever it was.
   */
  codes: z
    .array(
      z.strictObject({
        position: z.int().min(0),
        code: z.string(),
        outcome: z.enum(['written', 'there', 'held', 'full']),
        holder: z.string().optional(),
      }),
    )
    .optional(),
})
export type ReceiptRecorded = z.output<typeof receiptRecordedCodec>

/**
 * The trip the receipt was recorded as — one only marked removed, which «Вернуть» may still bring back,
 * too — or none: not recorded (a trip removed for good takes the receipt, MOL-240, and the answer is
 * 404); read once no «Записать» of it is still running on
 * the server (MOL-169, adversarial Г1): what «Отменить запись» asks before it lets a begun record go,
 * and where the review goes when it is recorded (round 4, Д1; review 8).
 */
export const receiptSettledCodec = z.strictObject({ tripId: z.uuid().nullable() })
export type ReceiptSettled = z.output<typeof receiptSettledCodec>

/**
 * The query of `GET /receipts` and `GET /receipts/:id` (MOL-129, adversarial А1): `shown=1` — the
 * phone asks with its page in view, so a receipt it is answered read is heard of in the app and
 * «чек разобран» will not follow. A list asked hidden — the connection back, a queue landing — says
 * nothing of the person, and leaves the bot to tell them.
 */
export const receiptReadQuerySchema = z.strictObject({ shown: z.literal('1').optional() })

/**
 * A phone that knows a review line's `code` says so by this header on `GET /receipts/:id` (MOL-234,
 * adversarial А3): a line with a code is refused by the strict codec of an installed app of an earlier
 * build, so the code goes only to a build that asked. A header, not the query: a server rolled back
 * refuses a query it does not know, and leaves a header alone.
 */
export const RECEIPT_CODES_HEADER = 'X-Molvia-Receipt-Codes'

/** A phone that knows the detail's `readTotal` says so by this header (MOL-244), as `RECEIPT_CODES_HEADER`. */
export const RECEIPT_READ_TOTAL_HEADER = 'X-Molvia-Receipt-Read-Total'

/** A phone that knows the detail's `readPartly` says so by this header (MOL-255), as `RECEIPT_CODES_HEADER`. */
export const RECEIPT_READ_PARTLY_HEADER = 'X-Molvia-Receipt-Read-Partly'

/**
 * «Переснять» removes the receipt it replaces by this header on `DELETE /receipts/:id` (MOL-309, owner's В-6 «а»):
 * the review of a receipt read not whole stays past it. A header, not the query: a server rolled back takes the
 * removal all the same, and the review goes as it did.
 */
export const RECEIPT_RETAKE_HEADER = 'X-Molvia-Receipt-Retake'

/** A phone that knows a line's `repaired` says so by this header (MOL-290), as `RECEIPT_CODES_HEADER`. */
export const RECEIPT_REPAIRED_HEADER = 'X-Molvia-Receipt-Repaired'

/** A phone that knows the detail's `cashback` and `redeemed` says so by this header (MOL-289). */
export const RECEIPT_CASHBACK_HEADER = 'X-Molvia-Receipt-Cashback'

/** A phone that knows a line's `spelled` says so by this header (MOL-293), as `RECEIPT_CODES_HEADER`. */
export const RECEIPT_SPELLED_HEADER = 'X-Molvia-Receipt-Spelled'

/** A phone that knows a line's `pack` says so by this header (MOL-264), as `RECEIPT_CODES_HEADER`. */
export const RECEIPT_PACK_HEADER = 'X-Molvia-Receipt-Pack'

/** A phone that knows the detail's `paidBy` says so by this header (MOL-312), as `RECEIPT_CODES_HEADER`. */
export const RECEIPT_PAID_BY_HEADER = 'X-Molvia-Receipt-Paid-By'
